import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Modal } from 'reactstrap';
import api from '../../../utils/api';
import './ModalRecording.css';

const summaryLabels = {
    keywords: 'AI 주요 키워드',
    keyword: 'AI 주요 키워드',
    topic: '주제',
    topics: '주제',
    call_contents: '통화내용',
    contents: '통화내용',
    decisions: '결정된 사항',
    decided: '결정된 사항',
    todo: '할 일',
    action_items: '할 일',
    timeline: '시간대별 주제',
    summary: '전체 요약',
};

function formatMilliseconds(value) {
    const seconds = Math.max(0, Math.floor(Number(value || 0) / 1000));
    const minutes = Math.floor(seconds / 60);
    return `${String(minutes).padStart(2, '0')}:${String(seconds % 60).padStart(2, '0')}`;
}

function formatSttTime(value) {
    if (!value || String(value).length < 14) return value || '';
    const text = String(value);
    return `${text.slice(0, 4)}-${text.slice(4, 6)}-${text.slice(6, 8)} ${text.slice(8, 10)}:${text.slice(10, 12)}:${text.slice(12, 14)}`;
}

function SummaryValue({ value }) {
    if (value === null || typeof value === 'undefined' || value === '') {
        return <span className="stt-summary-empty">등록된 내용이 없습니다.</span>;
    }
    if (Array.isArray(value)) {
        return (
            <ul className="stt-summary-list">
                {value.map((item, index) => <li key={index}><SummaryValue value={item}/></li>)}
            </ul>
        );
    }
    if (typeof value === 'object') {
        return (
            <div className="stt-summary-object">
                {Object.entries(value).map(([key, item]) => (
                    <div key={key} className="stt-summary-object-row">
                        <strong>{summaryLabels[key] || key}</strong>
                        <SummaryValue value={item}/>
                    </div>
                ))}
            </div>
        );
    }
    return <span>{String(value)}</span>;
}

function fileNameFromHeaders(headers, fallback) {
    const disposition = headers && headers['content-disposition'];
    const matched = disposition && disposition.match(/filename\*?=(?:UTF-8''|["']?)([^;"']+)/i);
    return matched ? decodeURIComponent(matched[1].replace(/["']/g, '')) : fallback;
}

function ModalRecording({ isOpen, toggle, recordingData }) {
    const audioRef = useRef(null);
    const [search, setSearch] = useState('');
    const [downloading, setDownloading] = useState('');
    const [actionError, setActionError] = useState('');

    const call = recordingData?.call || {};
    const record = recordingData?.record || {};
    const sttData = record.stt_data || {};
    const summary = record.stt_summary;
    const recordUrl = recordingData?.record_url;
    const segments = useMemo(
        () => Array.isArray(record.stt_data?.segments) ? record.stt_data.segments : [],
        [record.stt_data]
    );

    const filteredSegments = useMemo(() => {
        const keyword = search.trim().toLowerCase();
        if (!keyword) return segments;
        return segments.filter(segment => String(segment?.[3] || '').toLowerCase().includes(keyword));
    }, [segments, search]);

    useEffect(() => {
        if (!isOpen) {
            setSearch('');
            setActionError('');
        }
    }, [isOpen]);

    const seekToSegment = segment => {
        if (!audioRef.current || !recordUrl) return;
        audioRef.current.currentTime = Number(segment[0] || 0) / 1000;
        const play = audioRef.current.play();
        if (play && play.catch) play.catch(() => {});
    };

    const download = async type => {
        if (!call.call_id || downloading) return;
        setDownloading(type);
        setActionError('');
        try {
            const response = type === 'audio'
                ? await api.telephone.downloadSttRecording(call.call_id)
                : await api.telephone.downloadSttTranscript(call.call_id);
            const fallback = type === 'audio'
                ? `stt-recording-${record.call_sequence}.mp3`
                : `stt-transcript-${record.call_sequence}.xlsx`;
            const url = window.URL.createObjectURL(new Blob([response.data]));
            const anchor = document.createElement('a');
            anchor.href = url;
            anchor.download = fileNameFromHeaders(response.headers, fallback);
            document.body.appendChild(anchor);
            anchor.click();
            anchor.remove();
            window.URL.revokeObjectURL(url);
        } catch (error) {
            setActionError(type === 'audio'
                ? '녹취 파일을 다운로드하지 못했습니다.'
                : '대화 전문을 다운로드하지 못했습니다.');
        } finally {
            setDownloading('');
        }
    };

    return (
        <Modal
            isOpen={isOpen}
            toggle={toggle}
            size="xl"
            className="modal-dialog-scrollable stt-recording-modal"
            wrapClassName="stt-recording-modal-wrap"
        >
            <div className="modal-content">
                <div className="modal-header stt-modal-header">
                    <div>
                        <h5 className="modal-title">{call.call_cust_name || call.call_tel || 'STT 상담 기록'}</h5>
                        <div className="stt-call-meta">
                            {formatSttTime(sttData.startTime) || call.call_date}
                            {sttData.direction && <span>{sttData.direction === 'I' ? '수신' : '발신'}</span>}
                            {record.call_sequence && <span>#{record.call_sequence}</span>}
                        </div>
                    </div>
                    <div className="stt-header-actions">
                        <button type="button" className="btn btn-sm btn-outline-secondary" onClick={() => download('transcript')} disabled={!recordingData?.transcript_available || !!downloading}>
                            <i className="fas fa-file-excel mr-1"/> 대화 전문
                        </button>
                        <button type="button" className="btn btn-sm btn-outline-secondary" onClick={() => download('audio')} disabled={!recordUrl || !!downloading}>
                            <i className="fas fa-download mr-1"/> 녹취 다운로드
                        </button>
                        <button type="button" className="close" onClick={toggle}><span aria-hidden="true">×</span></button>
                    </div>
                </div>

                <div className="modal-body stt-modal-body">
                    <aside className="stt-summary-panel">
                        <div className="stt-summary-title"><i className="fas fa-magic"/> AI 상담 요약</div>
                        {summary ? (
                            typeof summary === 'object' && !Array.isArray(summary)
                                ? Object.entries(summary).map(([key, value]) => (
                                    <section className="stt-summary-section" key={key}>
                                        <h6>{summaryLabels[key] || key}</h6>
                                        <SummaryValue value={value}/>
                                    </section>
                                ))
                                : <section className="stt-summary-section"><SummaryValue value={summary}/></section>
                        ) : (
                            <div className="stt-summary-placeholder">
                                요약 정보가 등록되면 이 영역에 주요 키워드, 주제, 결정사항과 할 일이 표시됩니다.
                            </div>
                        )}
                    </aside>

                    <section className="stt-conversation-panel">
                        <div className="stt-conversation-toolbar">
                            <strong>음성기록 <span>{segments.length}</span></strong>
                            <div className="stt-search-box">
                                <input value={search} onChange={event => setSearch(event.target.value)} placeholder="대화 내용 검색"/>
                                <i className="fas fa-search"/>
                            </div>
                        </div>

                        <div className="stt-thread">
                            {filteredSegments.length > 0 ? filteredSegments.map((segment, index) => {
                                const isAgent = Number(segment[2]) === 0;
                                return (
                                    <button
                                        type="button"
                                        key={`${segment[0]}-${index}`}
                                        className={`stt-message ${isAgent ? 'agent' : 'customer'}`}
                                        onClick={() => seekToSegment(segment)}
                                        title={`${formatMilliseconds(segment[0])}부터 재생`}
                                    >
                                        <span className="stt-speaker">{isAgent ? '상담원' : '고객'} · {formatMilliseconds(segment[0])}</span>
                                        <span className="stt-message-text">{segment[3]}</span>
                                        <i className="fas fa-play stt-message-play"/>
                                    </button>
                                );
                            }) : (
                                <div className="stt-no-data">
                                    {search
                                        ? '검색 결과가 없습니다.'
                                        : recordingData?.recovered_recording
                                            ? 'STT 원문을 복구할 수 없어 녹취만 재생할 수 있습니다.'
                                            : '대화 내용이 없습니다.'}
                                </div>
                            )}
                        </div>

                        <div className="stt-player">
                            {actionError && <div className="stt-action-error">{actionError}</div>}
                            {recordUrl ? (
                                <audio key={recordUrl} ref={audioRef} controls preload="metadata" src={recordUrl}>
                                    Audio tag is unsupported in this browser.
                                </audio>
                            ) : (
                                <div className="stt-record-error">{recordingData?.record_error || '녹취 파일 URL을 가져올 수 없습니다.'}</div>
                            )}
                        </div>
                    </section>
                </div>
            </div>
        </Modal>
    );
}

export default ModalRecording;
